<template>
  <div class="custom-scrollbar">
    <main class="editor">
      <Header>
        <router-link
          id="back-to-modals"
          :to="{ name: 'modallist' }"
          class="header__action"
        >
          Pop-ups
        </router-link>
        <router-link
          v-show="!checkingSeo"
          id="back-to-pages"
          :to="{ name: 'pagelist' }"
          class="header__action"
        >
          <IconArrow direction="left" />
          Voltar para páginas
        </router-link>
        <button
          v-show="checkingSeo"
          class="header__action"
          @click="closeSeo"
        >
          <IconArrow direction="left" />
          Voltar para o editor
        </button>
        <div class="header__page-title">
          <span>{{ activePage.title }}</span>
          <IconLanguage
            v-if="hasTranslations"
            :language="activePage.language"
          />
        </div>
        <button
          v-show="pageIsDirty && isPagePublished"
          id="discard"
          class="header__action discard-changes"
          :disabled="isViewerUser"
          @click="toggleDiscardModal()"
        >
          <IconRestore />
          Descartar alterações
        </button>
        <button
          id="notifications"
          class="header__notification"
          @click="toggleValidationPanel()"
        >
          <IconNotifications />
          <span
            v-show="hasValidationMessages"
            class="header__notification__count"
          >
            {{ validationMessagesCount }}
          </span>
        </button>
        <div
          class="preview__desktop"
          @mouseenter="togglePreviewOptions"
          @mouseleave="togglePreviewOptions"
        >
          <div class="preview__icon header__action">
            <IconDesktop size="xs" />
            Desktop
          </div>
          <div
            v-if="showPreviewOptions"
            class="list-wrapper"
          >
            <ul class="list">
              <li class="item">
                <a
                  :href="previewUrl"
                  target="_blank"
                >
                  <IconEye size="xs" />
                  <span class="--margin-left">Visualizar página</span>
                </a>
              </li>
              <li class="item">
                <button @click="copyPreviewUrl">
                  <IconShare size="xs" />
                  <span class="--margin-left">
                    {{
                      previewUrlCopied
                        ? 'Link copiado!'
                        : 'Compartilhar rascunho'
                    }}
                  </span>
                </button>
              </li>
            </ul>
          </div>
        </div>
        <router-link
          id="preview"
          :to="{ name: 'preview' }"
          class="preview__icon header__action"
        >
          <IconSmartphone size="xs" />
          Mobile
        </router-link>
        <button
          id="publish"
          class="header__action blue-bg is-uppercase"
          :disabled="!pageIsDirty || isViewerUser"
          :title="publishButtonAltText"
          @click="publishHandler()"
        >
          {{ publishButtonText }}
        </button>
      </Header>

      <!-- Modal "Publicar" / "Despublicar" -->
      <div
        v-show="isPageBeingPublished"
        class="editor__publish-page-modal"
        @click="togglePublishingModal()"
      >
        <div
          class="editor__publish-page-modal__content"
          @click.stop
        >
          <div class="editor__publish-page-modal__content__header">
            <label
              class="btn-close"
              @click="togglePublishingModal()"
            >
              <IconClose />
            </label>
            <IconRocket
              class="text-warning"
              size="lg"
            />
            <h2>Publicar</h2>
          </div>

          <div class="editor__publish-page-modal__content__text">
            <p
              v-show="!hasValidationMessages"
              class="editor__publish-page-modal__content__text__no-errors"
            >
              Ao <b>publicar</b> sua página, suas alterações vão poder ser
              acessadas pelos seus usuários. Deseja continuar?
            </p>

            <p
              v-show="hasValidationMessages"
              class="editor__publish-page-modal__content__text__has-errors"
            >
              Você tem
              <b class="has-error"
                >{{ validationMessagesCount }}
                {{
                  singularOrPlural(
                    validationMessagesCount,
                    'item pendente',
                    'itens pendentes',
                  )
                }}</b
              >
              que
              {{ singularOrPlural(validationMessagesCount, 'pode', 'podem') }}
              prejudicar a saúde da página. Revise os itens e publique
              novamente.
            </p>
          </div>

          <div
            v-show="!hasValidationMessages"
            class="editor__publish-page-modal__content__buttons"
          >
            <button
              class="btn btn-secondary btn-white"
              type="button"
              @click="togglePublishingModal()"
            >
              Não, cancelar
            </button>
            <button
              class="btn btn-secondary"
              type="submit"
              @click="togglePageStatus()"
            >
              Sim, quero publicar!
            </button>
          </div>

          <div
            v-show="hasValidationMessages"
            class="editor__publish-page-modal__content__buttons"
          >
            <button
              class="btn btn-secondary"
              type="button"
              @click="toggleValidationPanel(true)"
            >
              Revisar
              {{
                singularOrPlural(
                  validationMessagesCount,
                  'item pendente',
                  'itens pendentes',
                )
              }}
            </button>
          </div>
        </div>
      </div>

      <!-- Modal "Descartar Alteração" -->
      <div
        v-show="isDiscardModalOpen"
        class="editor__publish-page-modal"
        @click="toggleDiscardModal()"
      >
        <div
          class="editor__publish-page-modal__content"
          @click.stop
        >
          <div class="editor__publish-page-modal__content__header">
            <label
              class="btn-close"
              @click="toggleDiscardModal()"
            >
              <IconClose />
            </label>
            <IconRestore
              class="text-danger"
              size="lg"
            />
            <h2>Deseja descartar as últimas modificações?</h2>
            <div class="editor__publish-page-modal__content__text">
              <p class="editor__publish-page-modal__content__text__no-errors">
                Ao descartar as alterações, a sua página irá retornar para o
                estado em que estava na última vez que foi publicada.
              </p>
            </div>

            <div class="editor__publish-page-modal__content__buttons">
              <button
                class="btn btn-neutral"
                type="button"
                @click="toggleDiscardModal()"
              >
                Voltar
              </button>
              <button
                class="btn btn-danger"
                type="submit"
                @click="discard()"
              >
                Descartar
              </button>
            </div>
          </div>
        </div>
      </div>
      <PresetList v-show="!checkingSeo" />
      <button
        v-show="!showPresetsContent && !checkingSeo"
        class="toggle-settings bg-white f-12 p-600 cl-gray-72 pointer"
        @click="toggleSettings()"
      >
        <IconSettings class="text-primary" />
        <span>Configurações</span>
      </button>
      <button
        v-show="!checkingSeo"
        class="toggle-seo bg-white f-12 p-600 cl-gray-72 pointer"
        @click="openSeo()"
      >
        <IconSeo class="text-primary" />
        <span>SEO</span>
      </button>
      <div class="editor__board">
        <SeoCheck v-if="checkingSeo" />
        <Canvas v-show="!checkingSeo" />
      </div>
      <InstanceProperties
        v-if="(!checkingSeo && activeInstance) || activeGlobalData"
      />
      <ValidationPanel
        v-show="showValidationPanel"
        @closeSeoViewer="checkingSeo = false"
      />
      <EditionFooter />
    </main>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';

import Canvas from '@/components/Canvas.vue';
import SeoCheck from '@/components/SeoCheck.vue';
import EditionFooter from '@/components/EditionFooter.vue';
import Header from '@/components/Header.vue';
import InstanceProperties from '@/components/InstanceProperties.vue';
import IconClose from '@/components/icons/IconClose.vue';
import IconArrow from '@/components/icons/IconArrow.vue';
import IconLanguage from '@/components/icons/IconLanguage.vue';
import IconNotifications from '@/components/icons/IconNotifications.vue';
import IconRocket from '@/components/icons/IconRocket.vue';
import IconRestore from '@/components/icons/IconRestore.vue';
import IconSettings from '@/components/icons/IconSettings.vue';
import IconSeo from '@/components/icons/IconSeo.vue';
import IconSmartphone from '@/components/icons/IconSmartphone.vue';
import IconDesktop from '@/components/icons/IconDesktop.vue';
import IconEye from '@/components/icons/IconEye.vue';
import IconShare from '@/components/icons/IconShare.vue';
import PresetList from '@/components/PresetList.vue';
import ValidationPanel from '@/components/ValidationPanel.vue';
import Toast from '@/helpers/Toast';

export default {
  name: 'Editor',
  components: {
    Canvas,
    SeoCheck,
    EditionFooter,
    Header,
    InstanceProperties,
    PresetList,
    ValidationPanel,
    IconClose,
    IconArrow,
    IconLanguage,
    IconNotifications,
    IconRocket,
    IconRestore,
    IconSettings,
    IconSeo,
    IconSmartphone,
    IconDesktop,
    IconEye,
    IconShare,
  },
  data() {
    return {
      Toast: null,
      checkingSeo: false,
      isPageBeingPublished: false,
      isDiscardModalOpen: false,
      showPreviewOptions: false,
      previewUrlCopied: false,
    };
  },
  computed: {
    hasTranslations() {
      return (
        this.activePage.translationIds && this.activePage.translationIds.length
      );
    },
    hasValidationMessages() {
      return this.validationMessagesCount > 0;
    },
    settingsInstance() {
      return this.activePage.settingsInstance;
    },
    isPagePublished() {
      return this.activePage.isPublished;
    },
    pageIsDirty() {
      // Handle legacy
      if (typeof this.activePage.isDirty !== 'boolean') {
        return !this.isPagePublished;
      }

      return this.activePage.isDirty;
    },
    publishButtonText() {
      return this.isPagePublished ? 'Publicar alterações' : 'Publicar';
    },
    publishButtonAltText() {
      return this.pageIsDirty
        ? this.publishButtonText
        : 'Não há alterações para publicar';
    },
    publishedUrlLink() {
      const { language, urlPath } = this.activePage;
      const isHome = urlPath === '_';
      const isTranslation = language && language !== 'pt';
      const path = isHome ? '' : urlPath;

      return isTranslation
        ? `${this.siteHostname}/${language}/${path}`
        : `${this.siteHostname}/${path}`;
    },
    previewUrl() {
      return this.activePage.previewUrl;
    },
    ...mapState([
      'customer',
      'customerSettings',
      'activeInstance',
      'activeGlobalData',
      'validationMessagesCount',
      'showValidationPanel',
      'showPresetsContent',
    ]),
    ...mapGetters(['activePage', 'siteHostname', 'isViewerUser']),
  },
  mounted() {
    this.$store.dispatch('saveActiveContainer');
    this.Toast = new Toast({
      customClass: {
        container: 'published-link',
      },
      position: 'top-end',
    });

    document.body.classList.remove('no-scroll');

    window.copyPublisedLink = () => {
      const copyText = document.getElementById('published-link');
      copyText.select();
      copyText.setSelectionRange(0, 99999);
      document.execCommand('copy');
    };
  },
  methods: {
    toggleSettings() {
      this.toggleActiveInstance(this.settingsInstance);
    },
    openSeo() {
      if (this.settingsInstance !== this.activeInstance) {
        this.toggleActiveInstance(this.settingsInstance);
      }
      this.checkingSeo = true;
    },
    closeSeo() {
      this.toggleActiveInstance(this.settingsInstance);
      this.checkingSeo = false;
    },
    publishHandler() {
      if (
        this.isPagePublished ||
        this.checkingSeo ||
        this.hasValidationMessages
      ) {
        this.togglePublishingModal();
      } else {
        this.openSeo();
      }
    },
    toastPublishedPage() {
      // TODO: Change svg to single icon component
      // which already exists and analyze this toast
      this.Toast.fire({
        showCloseButton: true,
        background: '#F2F2F2',
        html: `
          <input class="published-link__input" value="https://${this.publishedUrlLink}" id="published-link" />
          <a
            class="published-link__link"
            href="https://${this.publishedUrlLink}"
            target="_blank"
          >
            ${this.publishedUrlLink}
          </a>
          <a
            class="published-link__copy-btn"
            onclick="copyPublisedLink()"
          >
            <svg aria-hidden="true" focusable="false" data-prefix="far" data-icon="copy" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" class="svg-inline--fa fa-copy fa-w-14 fa-2x"><path fill="currentColor" d="M433.941 65.941l-51.882-51.882A48 48 0 0 0 348.118 0H176c-26.51 0-48 21.49-48 48v48H48c-26.51 0-48 21.49-48 48v320c0 26.51 21.49 48 48 48h224c26.51 0 48-21.49 48-48v-48h80c26.51 0 48-21.49 48-48V99.882a48 48 0 0 0-14.059-33.941zM266 464H54a6 6 0 0 1-6-6V150a6 6 0 0 1 6-6h74v224c0 26.51 21.49 48 48 48h96v42a6 6 0 0 1-6 6zm128-96H182a6 6 0 0 1-6-6V54a6 6 0 0 1 6-6h106v88c0 13.255 10.745 24 24 24h88v202a6 6 0 0 1-6 6zm6-256h-64V48h9.632c1.591 0 3.117.632 4.243 1.757l48.368 48.368a6 6 0 0 1 1.757 4.243V112z" class=""></path></svg>
          </a>
        `,
      });
    },
    togglePageStatus() {
      this.isPageBeingPublished = false;

      this.publishActiveContainer();
      this.toastPublishedPage();
    },
    singularOrPlural(count, singular, plural) {
      if (Math.abs(count) <= 1) {
        return singular;
      }

      return plural;
    },
    togglePublishingModal() {
      this.isPageBeingPublished = !this.isPageBeingPublished;

      if (this.isPageBeingPublished) {
        this.validateActiveContainer();
      }
    },
    toggleValidationPanel(showValidationPanel) {
      this.validateActiveContainer();
      this.toggleValidationPanelDisplay(showValidationPanel);

      this.isPageBeingPublished = false;
    },
    toggleDiscardModal() {
      this.isDiscardModalOpen = !this.isDiscardModalOpen;
    },
    discard() {
      this.discardChanges(this.activePage).then(() => {
        this.toggleDiscardModal();
      });
    },
    togglePreviewOptions() {
      this.previewUrlCopied = false;
      this.showPreviewOptions = !this.showPreviewOptions;
    },
    copyPreviewUrl() {
      this.previewUrlCopied = true;

      navigator.clipboard.writeText(this.previewUrl);

      setTimeout(() => {
        this.previewUrlCopied = false;
      }, 3000);
    },
    ...mapActions([
      'toggleActiveInstance',
      'publishActiveContainer',
      'toggleValidationPanelDisplay',
      'validateActiveContainer',
      'discardChanges',
    ]),
  },
};
</script>

<style lang="scss">
.published-link {
  margin: 56px 24px;

  .swal2-toast {
    padding: 8px 12px 16px 12px;
  }

  &__input {
    position: absolute;
    margin-left: -9999999px;
  }

  &__link {
    color: $lb-light-gray;
    text-decoration: underline;
  }

  &__copy-btn {
    width: 20px;
    display: inline-block;
    margin-left: 12px;
    cursor: pointer;
  }
}
</style>

<style lang="scss" scoped>
.custom-scrollbar {
  height: calc(100vh - 70px);
  margin: 45px 0 0 0;
}

.editor {
  &__board {
    width: 100%;
    max-width: 1260px;
    margin: 0 auto 40px;
    transition: all 0.3s ease-in-out;

    &--open-preset {
      width: calc(100vw - 160px - 100px);
      margin-left: 220px;
    }

    &--open-properties {
      width: calc(100vw - 287px - 200px);
    }
  }

  &__publish-page-modal {
    position: fixed;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    background-color: rgba($lb-dark-gray, 0.4);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9;
    &__content {
      width: 545px;
      display: flex;
      padding: 40px;
      flex-direction: column;
      background-color: $lb-white;
      align-items: center;
      &__header {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        flex-direction: column;
        position: relative;
        h2 {
          font-size: $size-bigger;
          font-weight: $lb-medium;
          line-height: 35px;
          text-align: center;
          letter-spacing: -1px;
          color: $gray-900;
          margin: 40px 0 20px 0;
        }
        .btn-close {
          position: absolute;
          top: 0;
          right: 0;
          cursor: pointer;
        }
      }

      &__text {
        display: flex;
        flex-direction: column;
        justify-content: center;
        text-align: center;
        line-height: 26px;
        color: $gray-900;
        margin-bottom: 20px;
        &__no-errors {
          font-size: $size-normal;
        }
        &__has-errors {
          font-size: $size-small;
        }
      }

      &__buttons {
        display: flex;
        justify-content: space-evenly;
        align-items: center;
        width: 70%;
      }
    }
  }

  .toggle-settings,
  .toggle-seo {
    height: 40px;
    width: 135px;
    border-radius: 20px;
    box-shadow: 0px 12px 24px rgba(0, 0, 0, 0.15);
    display: flex;
    justify-content: left;
    align-items: center;
    transition: box-shadow 0.3s ease-in-out;
    white-space: nowrap;
    position: fixed;
    top: 120px;
    left: 15px;
    border: initial;
    outline: none !important;
    z-index: 3;

    .icon {
      margin: 0 10px;
    }

    &:hover {
      border: 1px solid $crm-blue-hover2;
      box-shadow: 0px 5px 10px fade-out($crm-blue-hover2, 0.6);

      .icon {
        opacity: 0.8;
      }

      span {
        color: $crm-blue-primary;
      }
    }
  }

  .toggle-seo {
    top: 180px;
  }

  .discard-changes {
    .icon {
      margin-right: 5px;
      flex-shrink: 0;
    }
  }
}

.is-uppercase {
  text-transform: uppercase;
}

.preview {
  &__icon {
    flex-direction: column;
    font-size: 11px;
    margin: 0 !important;
  }

  &__desktop {
    display: flex;
    position: relative;

    .list-wrapper {
      width: max-content;
      position: absolute;
      top: 45px;
      padding-top: 8px;

      .list {
        padding: 35px;
        background-color: #535353;
        border-radius: 24px;
        color: #ffffff;
        font-size: 16px;
        font-weight: 500;

        .item {
          button,
          a {
            display: flex;
            justify-content: flex-start;
            align-items: center;
            color: inherit;

            .--margin-left {
              padding-left: 8px;
            }
          }

          button {
            outline: unset;
          }

          a {
            text-decoration: none;
          }

          + .item {
            margin-top: 16px;
          }

          &:hover {
            color: #3e9aa6;
          }
        }
      }
    }
  }
}
</style>
